/**
 * Developer-docs content model + data.
 *
 * The prose (titles, summaries, returns) are Lingui ``MessageDescriptor``s
 * resolved with ``i18n._()`` at render time — same pattern as the console
 * nav in ``ConsoleShell``. CODE is never translated: request/response
 * shapes and the curl / SDK examples are raw strings (the snippet
 * convention from ``lib/snippets.ts``).
 *
 * SOURCE OF TRUTH:
 *   - REST surface  → docs/PLATFORM_API_CONTRACT.md + docs/PLATFORM_CONSOLE_API.md
 *   - Python SDK → sdk/python/ando_ai/{client,connectors}.py
 *   - TypeScript SDK → sdk/typescript/src/client.ts
 * Keep the examples aligned with those shipped clients.
 */

import type { MessageDescriptor } from "@lingui/core";
import { msg } from "@lingui/core/macro";

import type { DocsCodeLang } from "./highlight";

export type DocsHttpMethod = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

/** One runnable snippet inside an endpoint / step (curl, python, json…). */
export interface DocsSnippet {
  lang: DocsCodeLang;
  /** Short label shown in the block header (e.g. "cURL", "Python", "202"). */
  label: string;
  code: string;
}

export interface DocsEndpoint {
  /** Stable anchor id (``ep-<name>``). */
  id: string;
  method: DocsHttpMethod;
  path: string;
  /** Required scope, ``"—"`` for public, or a session note. */
  scope: string;
  title: MessageDescriptor;
  summary: MessageDescriptor;
  request?: DocsSnippet;
  response?: DocsSnippet;
  examples: DocsSnippet[];
}

export interface DocsApiArea {
  id: string;
  title: MessageDescriptor;
  summary: MessageDescriptor;
  /** Auth line shown once per area (Bearer key vs session cookie). */
  auth: MessageDescriptor;
  endpoints: DocsEndpoint[];
}

export interface DocsSdkMethod {
  id: string;
  /** Raw SDK signature, matching the language of the example. */
  signature: string;
  summary: MessageDescriptor;
  /** Return SHAPE — code, not translated (rendered in monospace). */
  returns: string;
  example: DocsSnippet;
}

export interface DocsSdkGroup {
  id: string;
  title: MessageDescriptor;
  summary: MessageDescriptor;
  methods: DocsSdkMethod[];
}

export interface DocsQuickstartStep {
  id: string;
  title: MessageDescriptor;
  body: MessageDescriptor;
  snippets: DocsSnippet[];
}

// ── Quickstart ───────────────────────────────────────────────────────────────

export const DOCS_QUICKSTART: readonly DocsQuickstartStep[] = [
  {
    id: "qs-key",
    title: msg`1. Ottieni una API key`,
    body: msg`Crea una chiave da AppKeys (o via API con scope keys:manage). Il segreto è mostrato una sola volta: salvalo subito in un secret manager. La chiave È il project — nessun parametro project negli endpoint.`,
    snippets: [
      {
        lang: "bash",
        label: "cURL",
        code: [
          'curl -s -X POST "$ANDO_BASE_URL/keys" \\',
          '  -H "Authorization: Bearer $ANDO_API_KEY" \\',
          '  -H "Content-Type: application/json" \\',
          `  -d '{"name": "quickstart", "scopes": ["ingest:write", "query:read"], "live": true}'`,
        ].join("\n"),
      },
      {
        lang: "python",
        label: "Python",
        code: [
          "from ando_ai import AndoPlatform",
          "",
          'admin = AndoPlatform("ando_sk_live_...")',
          'key = admin.create_key("quickstart", ["ingest:write", "query:read"], live=True)',
          'print(key["api_key"])  # shown once — store it now',
        ].join("\n"),
      },
    ],
  },
  {
    id: "qs-setup",
    title: msg`2. Configura l'ambiente e verifica la chiave`,
    body: msg`Esporta base URL e chiave, poi chiama GET /me: risponde con l'identità del project (tenant, scope, tier). Se la chiave è valida, sei pronto.`,
    snippets: [
      {
        lang: "bash",
        label: "shell",
        code: [
          'export ANDO_BASE_URL="https://api.ando-ai.com/platform/v1"',
          'export ANDO_API_KEY="ando_sk_live_..."   # la tua chiave',
          "",
          'curl -s "$ANDO_BASE_URL/me" -H "Authorization: Bearer $ANDO_API_KEY"',
        ].join("\n"),
      },
      {
        lang: "python",
        label: "Python",
        code: [
          "from ando_ai import AndoPlatform",
          "",
          'with AndoPlatform("ando_sk_live_...") as ando:',
          "    print(ando.me())",
        ].join("\n"),
      },
    ],
  },
  {
    id: "qs-ingest",
    title: msg`3. Ingerisci il primo documento`,
    body: msg`L'ingestion è sempre asincrona: l'upload risponde 202 con un job_id. wait_for_job fa il polling fino a completed (o solleva JobFailed con l'error_code stabile).`,
    snippets: [
      {
        lang: "bash",
        label: "cURL",
        code: [
          'curl -s -X POST "$ANDO_BASE_URL/documents" \\',
          '  -H "Authorization: Bearer $ANDO_API_KEY" \\',
          '  -F "file=@contratto.pdf"',
        ].join("\n"),
      },
      {
        lang: "python",
        label: "Python",
        code: [
          "from ando_ai import AndoPlatform, JobFailed",
          "",
          'with AndoPlatform("ando_sk_live_...") as ando:',
          '    accepted = ando.upload_document("contratto.pdf")',
          "    try:",
          '        ando.wait_for_job(accepted["job_id"], timeout=120, poll=2.0)',
          "    except JobFailed as exc:",
          "        print(exc.error_code, exc.error_message)",
          "        raise",
        ].join("\n"),
      },
    ],
  },
  {
    id: "qs-query",
    title: msg`4. Interroga: retrieve e answer`,
    body: msg`mode=retrieve restituisce i chunk rilevanti con score e citazioni (fai tu la generazione). mode=answer sintetizza una risposta ancorata, rispecchiando la lingua della domanda; se non trova nulla, answer è null e no_results è true.`,
    snippets: [
      {
        lang: "python",
        label: "Python",
        code: [
          "# chunks + score (mode=retrieve)",
          'hits = ando.query("Qual è la durata del contratto?", k=5)',
          'for item in hits["results"]:',
          '    print(item["score"], item["text"][:80])',
          "",
          "# risposta sintetizzata (mode=answer)",
          'result = ando.answer("Qual è la durata del contratto?", k=5)',
          'print(result["answer"], result["citations"])',
        ].join("\n"),
      },
      {
        lang: "bash",
        label: "cURL",
        code: [
          'curl -s -X POST "$ANDO_BASE_URL/query" \\',
          '  -H "Authorization: Bearer $ANDO_API_KEY" \\',
          '  -H "Content-Type: application/json" \\',
          `  -d '{"query": "Qual è la durata del contratto?", "mode": "answer", "k": 5}'`,
        ].join("\n"),
      },
    ],
  },
  {
    id: "qs-agentic",
    title: msg`5. Agentic answer (PREVIEW)`,
    body: msg`agentic_answer esegue l'intero loop Ando/TRAMA: escalation T1→T2→T3 e, sulle sources strutturate pubblicate, text-to-SQL. Read-only per costruzione. Restringere a un connettore rende la corsa più affidabile (profondità invece di ampiezza).`,
    snippets: [
      {
        lang: "python",
        label: "Python",
        code: [
          "res = ando.agentic_answer(",
          '    "Quali clienti hanno l\'esposizione più critica?",',
          '    sources=["structured"],',
          "    with_diagnostics=True,",
          ")",
          'print(res["answer"])',
          'for step in res["steps"]:',
          '    print(step["step"], step["type"], step["status"])',
        ].join("\n"),
      },
    ],
  },
] as const;

// ── API reference ─────────────────────────────────────────────────────────────

const AREA_CORE: DocsApiArea = {
  id: "api-core",
  title: msg`Core — identità & chiavi`,
  summary: msg`Liveness, identità del project e gestione delle AppKey. Ogni chiave ha scope espliciti e appartiene a un project isolato.`,
  auth: msg`Runtime · Authorization: Bearer <api_key>`,
  endpoints: [
    {
      id: "ep-health",
      method: "GET",
      path: "/health",
      scope: "—",
      title: msg`Liveness pubblica`,
      summary: msg`Probe di liveness senza autenticazione.`,
      examples: [
        { lang: "python", label: "Python", code: "ando.health()  # {'status': 'ok'}" },
        {
          lang: "bash",
          label: "cURL",
          code: 'curl -s "$ANDO_BASE_URL/health"',
        },
      ],
    },
    {
      id: "ep-me",
      method: "GET",
      path: "/me",
      scope: "chiave valida",
      title: msg`Identità del project`,
      summary: msg`Tenant, scope e rate-limit tier della chiave corrente.`,
      response: {
        lang: "json",
        label: "200",
        code: [
          "{",
          '  "project": {',
          '    "tenant_id": "9f1c2e3a-…",',
          '    "scopes": ["ingest:write", "query:read"],',
          '    "rate_limit_tier": "standard"',
          "  }",
          "}",
        ].join("\n"),
      },
      examples: [{ lang: "python", label: "Python", code: "ando.me()" }],
    },
    {
      id: "ep-keys-create",
      method: "POST",
      path: "/keys",
      scope: "keys:manage",
      title: msg`Crea una chiave`,
      summary: msg`Conia una AppKey; il segreto è nella risposta una sola volta. live=false conia una chiave ando_sk_test_. principal_mode=service è il default. Per gli utenti di una nuova app usa app_user_server o app_user_oidc con app_id; app_user_oidc richiede anche end_user_issuer. La chiave rimane sul backend dell'app, condivisa dai suoi utenti autenticati. resource_policy (opzionale) restringe la chiave: sources, connector_ids, action_connectors, cad_visibility e monthly_spend_limit_cents — lo SPEND-CAP mensile per-chiave in centesimi USD (superata la soglia, la chiave riceve spend_limit_exceeded).`,
      request: {
        lang: "json",
        label: "body",
        code: [
          "{",
          '  "name": "ingest-worker",',
          '  "scopes": ["ingest:write", "query:read"],',
          '  "live": true,',
          '  "resource_policy": {',
          '    "sources": ["documents"],',
          '    "monthly_spend_limit_cents": 5000',
          "  }",
          "}",
        ].join("\n"),
      },
      examples: [
        {
          lang: "python",
          label: "Python",
          code: 'ando.create_key("ingest-worker", ["ingest:write", "query:read"], live=True)',
        },
      ],
    },
    {
      id: "ep-keys-list",
      method: "GET",
      path: "/keys",
      scope: "keys:manage",
      title: msg`Elenca le chiavi`,
      summary: msg`Campi display-safe (mai l'hash); paginato con ?limit=&cursor=.`,
      examples: [{ lang: "python", label: "Python", code: "ando.list_keys()" }],
    },
    {
      id: "ep-keys-revoke",
      method: "POST",
      path: "/keys/{key_id}/revoke",
      scope: "keys:manage",
      title: msg`Revoca una chiave`,
      summary: msg`Revoca immediata; una chiave revocata risponde revoked_api_key.`,
      examples: [{ lang: "python", label: "Python", code: 'ando.revoke_key("key_...")' }],
    },
  ],
};

const AREA_QUERY: DocsApiArea = {
  id: "api-query",
  title: msg`Query & Agentic answer`,
  summary: msg`Retrieval sui chunk (/query) e il loop agentico completo con text-to-SQL sulle sources strutturate (/agentic/answer, PREVIEW).`,
  auth: msg`Runtime · Authorization: Bearer <api_key>`,
  endpoints: [
    {
      id: "ep-query",
      method: "POST",
      path: "/query",
      scope: "query:read",
      title: msg`Retrieval / answer`,
      summary: msg`mode=retrieve → chunk con score + citazioni; mode=answer → risposta sintetizzata. k 1..50 (default 10). filters, include_web e verify restringono/arricchiscono; la resource policy della chiave vince sempre.`,
      request: {
        lang: "json",
        label: "body",
        code: [
          "{",
          '  "query": "Qual è la durata del contratto?",',
          '  "mode": "answer",',
          '  "k": 5,',
          '  "filters": {"sources": ["fs"], "partition": "cliente-42"}',
          "}",
        ].join("\n"),
      },
      response: {
        lang: "json",
        label: "200",
        code: [
          "{",
          '  "results": [{"document_id": "…", "chunk_id": "…", "text": "…", "score": 0.91, "source": "fs"}],',
          '  "answer": "Il contratto ha durata di 36 mesi…",',
          '  "citations": [{"document_id": "…", "chunk_id": "…"}],',
          '  "no_results": false',
          "}",
        ].join("\n"),
      },
      examples: [
        {
          lang: "python",
          label: "Python",
          code: [
            'ando.query("durata del contratto?", mode="retrieve", k=5)',
            'ando.answer("durata del contratto?", k=5, sources=["fs"])',
          ].join("\n"),
        },
        {
          lang: "bash",
          label: "cURL",
          code: [
            'curl -s -X POST "$ANDO_BASE_URL/query" \\',
            '  -H "Authorization: Bearer $ANDO_API_KEY" \\',
            '  -H "Content-Type: application/json" \\',
            `  -d '{"query": "durata del contratto?", "mode": "answer", "k": 5}'`,
          ].join("\n"),
        },
      ],
    },
    {
      id: "ep-agentic",
      method: "POST",
      path: "/agentic/answer",
      scope: "query:read",
      title: msg`Agentic answer`,
      summary: msg`Il loop agentico read-only: escalation TRAMA + text-to-SQL sulle sources pubblicate. sources accetta bucket (documents/structured/web) e id concreti mescolati. Passa conversation_id per il multi-turn. Latenza in decine di secondi (timeout dedicato 120s).`,
      request: {
        lang: "json",
        label: "body",
        code: [
          "{",
          '  "query": "Quali clienti hanno esposizione più critica?",',
          '  "k": 25,',
          '  "sources": ["structured", "conn_7c3e9d5a-…"],',
          '  "with_diagnostics": false',
          "}",
        ].join("\n"),
      },
      response: {
        lang: "json",
        label: "200",
        code: [
          "{",
          '  "answer": "I tre clienti più esposti sono …",',
          '  "citations": [{"document_id": "…", "chunk_id": "…"}],',
          '  "sources_used": ["documents", "structured"],',
          '  "steps": [{"step": 4, "type": "sql", "status": "ok", "row_count": 12}],',
          '  "diagnostics": {"path": "orchestrator", "tier": "T1", "focus": "structured"}',
          "}",
        ].join("\n"),
      },
      examples: [
        {
          lang: "python",
          label: "Python",
          code: [
            "res = ando.agentic_answer(",
            '    "Quali clienti sono più critici?",',
            '    sources=["structured"],',
            "    with_diagnostics=True,",
            ")",
            'print(res["answer"], res["sources_used"])',
          ].join("\n"),
        },
      ],
    },
  ],
};

const AREA_DOCUMENTS: DocsApiArea = {
  id: "api-documents",
  title: msg`Documenti & ingestion`,
  summary: msg`Upload (multipart o source_url), stato del documento e polling del job. Ingestion sempre asincrona: 202 + job_id.`,
  auth: msg`Runtime · Authorization: Bearer <api_key>`,
  endpoints: [
    {
      id: "ep-doc-upload",
      method: "POST",
      path: "/documents",
      scope: "ingest:write",
      title: msg`Carica un documento`,
      summary: msg`multipart (parte file) oppure un JSON con source_url. metadata (oggetto piatto, max 32 chiavi) e partition sono propagati a ogni chunk e filtrabili in /query. Formati: PDF, DOCX, XLSX, PPTX, HTML, TXT, MD, PNG, JPEG, WEBP (max 25 MB). Idempotency-Key opt-in.`,
      response: {
        lang: "json",
        label: "202",
        code: '{"document_id": "1d4f0a9c-…", "job_id": "e8a1b6d2-…", "status": "queued"}',
      },
      examples: [
        {
          lang: "python",
          label: "Python",
          code: [
            'ando.upload_document("contratto.pdf",',
            '                     metadata={"doc_type": "invoice", "year": 2026},',
            '                     partition="cliente-42")',
          ].join("\n"),
        },
        {
          lang: "bash",
          label: "cURL",
          code: [
            'curl -s -X POST "$ANDO_BASE_URL/documents" \\',
            '  -H "Authorization: Bearer $ANDO_API_KEY" \\',
            '  -F "file=@contratto.pdf" \\',
            `  -F 'metadata={"doc_type":"invoice"}'`,
          ].join("\n"),
        },
      ],
    },
    {
      id: "ep-doc-get",
      method: "GET",
      path: "/documents/{id}",
      scope: "query:read",
      title: msg`Stato del documento`,
      summary: msg`Stato + metadati del documento ingerito.`,
      examples: [{ lang: "python", label: "Python", code: 'ando.get_document("1d4f0a9c-…")' }],
    },
    {
      id: "ep-doc-delete",
      method: "DELETE",
      path: "/documents/{id}",
      scope: "ingest:write",
      title: msg`Elimina il documento`,
      summary: msg`Rimuove indice + storage. L'aggiornamento in-place oggi è DELETE + POST.`,
      examples: [{ lang: "python", label: "Python", code: 'ando.delete_document("1d4f0a9c-…")' }],
    },
    {
      id: "ep-job-get",
      method: "GET",
      path: "/jobs/{id}",
      scope: "query:read",
      title: msg`Stato del job`,
      summary: msg`queued → processing → completed | failed (+ error_code stabile). L'SDK offre wait_for_job con polling e retry sui transienti.`,
      examples: [
        {
          lang: "python",
          label: "Python",
          code: [
            'ando.get_job("e8a1b6d2-…")',
            'ando.wait_for_job("e8a1b6d2-…", timeout=120, poll=2.0)',
          ].join("\n"),
        },
      ],
    },
  ],
};

const AREA_CONVERSATIONS: DocsApiArea = {
  id: "api-conversations",
  title: msg`Conversazioni (multi-turn)`,
  summary: msg`Stato conversazionale per gli agentic turn: la seconda domanda ricorda la prima. È la STESSA entità che usa la chat del prodotto.`,
  auth: msg`Runtime · Authorization: Bearer <api_key>`,
  endpoints: [
    {
      id: "ep-conv-create",
      method: "POST",
      path: "/conversations",
      scope: "query:read",
      title: msg`Apri una conversazione`,
      summary: msg`Corpo opzionale con un title. Senza titolo, il primo turno la intitola con la tua prima riga verbatim.`,
      response: {
        lang: "json",
        label: "201",
        code: '{"conversation_id": "9f1c8d2e-…", "title": "…", "message_count": 0}',
      },
      examples: [{ lang: "python", label: "Python", code: "conv = ando.create_conversation()" }],
    },
    {
      id: "ep-conv-list",
      method: "GET",
      path: "/conversations",
      scope: "query:read",
      title: msg`Elenca le conversazioni`,
      summary: msg`Più recenti prima; cursore opaco ?limit=&cursor=.`,
      examples: [{ lang: "python", label: "Python", code: "ando.list_conversations(limit=25)" }],
    },
    {
      id: "ep-conv-turns",
      method: "GET",
      path: "/conversations/{id}",
      scope: "query:read",
      title: msg`Trascrizione`,
      summary: msg`I turni dal più vecchio (cronologico), paginati. role = user | assistant, query_id lega le due metà.`,
      examples: [
        {
          lang: "python",
          label: "Python",
          code: 'ando.get_conversation_turns("9f1c8d2e-…", limit=50)',
        },
      ],
    },
    {
      id: "ep-conv-delete",
      method: "DELETE",
      path: "/conversations/{id}",
      scope: "query:read",
      title: msg`Cancella`,
      summary: msg`Irreversibile: i turni sono rimossi e l'id è ritirato (poi not_found).`,
      examples: [
        { lang: "python", label: "Python", code: 'ando.delete_conversation("9f1c8d2e-…")' },
      ],
    },
    {
      id: "ep-conv-multiturn",
      method: "POST",
      path: "/agentic/answer",
      scope: "query:read",
      title: msg`Flusso multi-turn`,
      summary: msg`Passa lo stesso conversation_id a più agentic_answer: il follow-up è reso autonomo con le entità della conversazione ("e il primo di quella lista?").`,
      examples: [
        {
          lang: "python",
          label: "Python",
          code: [
            "conv = ando.create_conversation()",
            'ando.agentic_answer("Quali clienti sono più critici?", conversation_id=conv["conversation_id"])',
            'ando.agentic_answer("E il primo di quella lista, da quanto?", conversation_id=conv["conversation_id"])',
          ].join("\n"),
        },
      ],
    },
  ],
};

const AREA_CONNECTORS: DocsApiArea = {
  id: "api-connectors",
  title: msg`Connectors (Push API)`,
  summary: msg`Registra un connettore e spingi record: la piattaforma fa chunking, embeddings, ACL, versioning e fusion. Il dev emette solo record. mode=full attiva il mark-and-sweep al complete. GET /connectors/kinds indica disponibilità e contratto dei kind: push e manifest disponibili, code non ancora attivabile.`,
  auth: msg`Runtime · Bearer <api_key> · scope sources:manage + ingest:write`,
  endpoints: [
    {
      id: "ep-conn-create",
      method: "POST",
      path: "/connectors",
      scope: "sources:manage",
      title: msg`Registra un connettore`,
      summary: msg`Corpo con slug, name e kind push. Il connettore appare in console accanto a quelli nativi.`,
      examples: [
        {
          lang: "python",
          label: "Python",
          code: ['crm = ando.connector("acme-crm")', 'crm.create("Acme CRM")'].join("\n"),
        },
      ],
    },
    {
      id: "ep-conn-create-manifest",
      method: "POST",
      path: "/connectors",
      scope: "sources:manage",
      title: msg`Registra un connettore manifest`,
      summary: msg`kind manifest: descrivi la tua API REST come JSON. Il sync periodico si lega alla AppKey di servizio live scelta alla registrazione e si ferma se la chiave scade o viene revocata; puoi ricollegarne esplicitamente una dello stesso utente di servizio. Controlla AppKeys e lo storico sync prima di attendere dati. Regole: base_url e token_url OAuth solo https pubblico; auth.secret è WRITE-ONLY (le letture mostrano secret_set: true); fields.external_id obbligatorio; cursor presente → sync incrementale, assente → full con mark-and-sweep; interval_minutes intero ≥ 15, default 60.`,
      request: {
        lang: "json",
        label: "config",
        code: JSON.stringify(
          {
            slug: "erp-articoli",
            name: "ERP Articoli",
            kind: "manifest",
            config: {
              manifest: {
                base_url: "https://erp.example.com/api",
                auth: { type: "header", header_name: "X-Api-Key", secret: "..." },
                resource: {
                  path: "/articles",
                  records_field: "items",
                  fields: {
                    external_id: "id",
                    title: "name",
                    content: "description",
                    updated_at: "modified_at",
                  },
                  cursor: { param: "cursor", response_field: "next_cursor" },
                },
                interval_minutes: 60,
              },
            },
          },
          null,
          2,
        ),
      },
      examples: [
        {
          lang: "bash",
          label: "cURL",
          code: [
            'curl -s -X POST "$ANDO_BASE_URL/connectors" \\',
            '  -H "Authorization: Bearer $ANDO_API_KEY" -H "Content-Type: application/json" \\',
            "  -d @manifest-connector.json",
          ].join("\n"),
        },
      ],
    },
    {
      id: "ep-conn-update",
      method: "PATCH",
      path: "/connectors/{slug}",
      scope: "sources:manage",
      title: msg`Aggiorna / riattiva / ruota il secret`,
      summary: msg`Ogni campo è opzionale: name rinomina; enabled: true riattiva un connettore disabilitato (il ledger sopravvive, l'incrementale riparte); config sostituisce la configurazione PER INTERO. Manifest: un nuovo manifest.auth.secret RUOTA il secret sigillato, ometterlo lo mantiene; lo stato del cursore si azzera (la run successiva è un full pulito).`,
      request: {
        lang: "json",
        label: "body",
        code: '{"name": "Acme CRM (prod)", "enabled": true}',
      },
      examples: [
        {
          lang: "bash",
          label: "cURL",
          code: [
            'curl -s -X PATCH "$ANDO_BASE_URL/connectors/acme-crm" \\',
            '  -H "Authorization: Bearer $ANDO_API_KEY" -H "Content-Type: application/json" \\',
            `  -d '{"enabled": true}'`,
          ].join("\n"),
        },
      ],
    },
    {
      id: "ep-conn-validate",
      method: "POST",
      path: "/connectors/validate",
      scope: "sources:manage",
      title: msg`Valida record (dry-run)`,
      summary: msg`Verifica un batch contro il Record contract SENZA scrivere: per ogni record torna l'anteprima di cosa verrebbe indicizzato oppure il rifiuto con index, field e reason. accepted + rejected = record inviati.`,
      request: {
        lang: "json",
        label: "body",
        code: '{"records": [{"external_id": "acme:deal:1", "content": "…"}]}',
      },
      examples: [
        {
          lang: "bash",
          label: "cURL",
          code: [
            'curl -s -X POST "$ANDO_BASE_URL/connectors/validate" \\',
            '  -H "Authorization: Bearer $ANDO_API_KEY" -H "Content-Type: application/json" \\',
            `  -d '{"records": [{"external_id": "acme:deal:1", "content": "…"}]}'`,
          ].join("\n"),
        },
      ],
    },
    {
      id: "ep-conn-list",
      method: "GET",
      path: "/connectors",
      scope: "sources:manage",
      title: msg`Elenca / dettaglio`,
      summary: msg`Tutti i connettori del project; GET /connectors/<slug> per il dettaglio.`,
      examples: [
        {
          lang: "python",
          label: "Python",
          code: ["ando.connectors()", 'ando.connector("acme-crm").get()'].join("\n"),
        },
      ],
    },
    {
      id: "ep-conn-sync",
      method: "POST",
      path: "/connectors/{slug}/syncs",
      scope: "sources:manage",
      title: msg`Ciclo di sync`,
      summary: msg`Apri una run (mode full o incremental), spingi batch (≤500 record, Idempotency-Key per batch) su .../records, poi .../complete. L'SDK AndoConnector fa batching, retry con backoff e abort-on-exception.`,
      request: {
        lang: "json",
        label: "records",
        code: '{"records": [{"external_id": "acme:deal:1", "kind": "record", "title": "…", "content": "…", "updated_at": "2026-07-20T08:12:00Z"}]}',
      },
      examples: [
        {
          lang: "python",
          label: "Python (one-shot)",
          code: [
            'result = crm.sync(fetch_deals(), mode="full")',
            "print(result.accepted, result.unchanged, result.rejected)",
          ].join("\n"),
        },
        {
          lang: "python",
          label: "Python (streaming)",
          code: [
            'with crm.open_sync(mode="incremental") as run:',
            "    for page in paginate():",
            "        run.push(page)",
            "# uscita pulita = complete; un'eccezione = abort (niente sweep parziale)",
          ].join("\n"),
        },
        {
          lang: "bash",
          label: "cURL",
          code: [
            "# 1) apri  2) push batch  3) complete",
            'SID=$(curl -s -X POST "$ANDO_BASE_URL/connectors/acme-crm/syncs" \\',
            '  -H "Authorization: Bearer $ANDO_API_KEY" -H "Content-Type: application/json" \\',
            `  -d '{"mode": "full"}' | jq -r .sync_id)`,
            'curl -s -X POST "$ANDO_BASE_URL/connectors/acme-crm/syncs/$SID/records" \\',
            '  -H "Authorization: Bearer $ANDO_API_KEY" -H "Content-Type: application/json" \\',
            '  -H "Idempotency-Key: $(uuidgen)" \\',
            `  -d '{"records": [{"external_id": "acme:deal:1", "kind": "record", "content": "…"}]}'`,
            'curl -s -X POST "$ANDO_BASE_URL/connectors/acme-crm/syncs/$SID/complete" \\',
            '  -H "Authorization: Bearer $ANDO_API_KEY"',
          ].join("\n"),
        },
      ],
    },
    {
      id: "ep-conn-history",
      method: "GET",
      path: "/connectors/{slug}/syncs",
      scope: "sources:manage",
      title: msg`Storico delle run`,
      summary: msg`Run più recenti prima, con stato, contatori, errori per record e durata.`,
      examples: [{ lang: "python", label: "Python", code: "crm.syncs(limit=20)" }],
    },
    {
      id: "ep-conn-disable",
      method: "DELETE",
      path: "/connectors/{slug}",
      scope: "sources:manage",
      title: msg`Disabilita`,
      summary: msg`Non distruttivo: il contenuto già indicizzato resta ricercabile.`,
      examples: [{ lang: "python", label: "Python", code: "crm.disable()" }],
    },
  ],
};

const AREA_SOURCES: DocsApiArea = {
  id: "api-sources",
  title: msg`Sources strutturate`,
  summary: msg`Colleghi un database read-only, curi il K-Schema proposto dall'AI e pubblichi: la source entra in /query e negli agentic turn. Una source non partecipa finché non è published.`,
  auth: msg`Runtime · Bearer <api_key> · scope sources:manage`,
  endpoints: [
    {
      id: "ep-src-db",
      method: "POST",
      path: "/sources/database",
      scope: "sources:manage",
      title: msg`Collega un database`,
      summary: msg`Credenziali read-only; avvia il profiling.`,
      examples: [
        {
          lang: "bash",
          label: "cURL",
          code: [
            'curl -s -X POST "$ANDO_BASE_URL/sources/database" \\',
            '  -H "Authorization: Bearer $ANDO_API_KEY" -H "Content-Type: application/json" \\',
            `  -d '{"engine": "postgres", "dsn": "postgres://readonly@host/db"}'`,
          ].join("\n"),
        },
      ],
    },
    {
      id: "ep-src-schema",
      method: "GET",
      path: "/sources/{id}/schema",
      scope: "sources:manage",
      title: msg`K-Schema proposto`,
      summary: msg`La bozza K-Schema da curare; PATCH per descrizioni/join/entità.`,
      examples: [
        {
          lang: "bash",
          label: "cURL",
          code: 'curl -s "$ANDO_BASE_URL/sources/$SRC/schema" -H "Authorization: Bearer $ANDO_API_KEY"',
        },
      ],
    },
    {
      id: "ep-src-publish",
      method: "POST",
      path: "/sources/{id}/publish",
      scope: "sources:manage",
      title: msg`Valida e pubblica`,
      summary: msg`POST /sources/<id>/validate prova una query NL → SQL preview; /publish porta la source in /query.`,
      examples: [
        {
          lang: "bash",
          label: "cURL",
          code: 'curl -s -X POST "$ANDO_BASE_URL/sources/$SRC/publish" -H "Authorization: Bearer $ANDO_API_KEY"',
        },
      ],
    },
  ],
};

const AREA_WEBHOOKS: DocsApiArea = {
  id: "api-webhooks",
  title: msg`Webhooks`,
  summary: msg`Ricevi eventi invece di fare polling: ingestion.completed, source.sync.completed, usage.threshold_reached e action.requested (il write-loop delle azioni dei connettori personalizzati). Payload firmati HMAC-SHA256 (Standard Webhooks), retry con backoff fino a 24h. Gestibili anche dalla console senza chiave: sezione Webhooks della pagina «Il tuo agente» (base sessione /platform/console/v1/webhooks).`,
  auth: msg`Runtime · Bearer <api_key> · scope keys:manage`,
  endpoints: [
    {
      id: "ep-wh-create",
      method: "POST",
      path: "/webhooks",
      scope: "keys:manage",
      title: msg`Registra un endpoint`,
      summary: msg`Il signing secret è nella risposta una sola volta.`,
      request: {
        lang: "json",
        label: "body",
        code: '{"url": "https://example.com/hooks/ando", "events": ["ingestion.completed"]}',
      },
      examples: [
        {
          lang: "bash",
          label: "cURL",
          code: [
            'curl -s -X POST "$ANDO_BASE_URL/webhooks" \\',
            '  -H "Authorization: Bearer $ANDO_API_KEY" -H "Content-Type: application/json" \\',
            `  -d '{"url": "https://example.com/hooks/ando", "events": ["ingestion.completed"]}'`,
          ].join("\n"),
        },
      ],
    },
    {
      id: "ep-wh-list",
      method: "GET",
      path: "/webhooks",
      scope: "keys:manage",
      title: msg`Elenca / rimuovi`,
      summary: msg`GET elenca gli endpoint; DELETE /webhooks/<id> li rimuove.`,
      examples: [
        {
          lang: "bash",
          label: "cURL",
          code: 'curl -s "$ANDO_BASE_URL/webhooks" -H "Authorization: Bearer $ANDO_API_KEY"',
        },
      ],
    },
    {
      id: "ep-wh-test",
      method: "POST",
      path: "/webhooks/{id}/test",
      scope: "keys:manage",
      title: msg`Consegna di prova`,
      summary: msg`Consegna un evento test.ping all'endpoint registrato.`,
      examples: [
        {
          lang: "bash",
          label: "cURL",
          code: 'curl -s -X POST "$ANDO_BASE_URL/webhooks/$WH/test" -H "Authorization: Bearer $ANDO_API_KEY"',
        },
      ],
    },
  ],
};

const AREA_GENERATE: DocsApiArea = {
  id: "api-generate",
  title: msg`Document generation`,
  summary: msg`Genera un documento dal contenuto del project → 202 + generation_id, poi polling e download.`,
  auth: msg`Runtime · Bearer <api_key> · scope generate:write`,
  endpoints: [
    {
      id: "ep-gen-create",
      method: "POST",
      path: "/generate",
      scope: "generate:write",
      title: msg`Genera un documento`,
      summary: msg`Restituisce 202 con generation_id e document_id.`,
      response: {
        lang: "json",
        label: "202",
        code: '{"generation_id": "…", "document_id": "…", "status": "queued"}',
      },
      examples: [
        {
          lang: "bash",
          label: "cURL",
          code: [
            'curl -s -X POST "$ANDO_BASE_URL/generate" \\',
            '  -H "Authorization: Bearer $ANDO_API_KEY" -H "Content-Type: application/json" \\',
            `  -d '{"prompt": "Riepilogo trimestrale", "format": "docx"}'`,
          ].join("\n"),
        },
      ],
    },
    {
      id: "ep-gen-status",
      method: "GET",
      path: "/generate/{generation_id}",
      scope: "generate:write",
      title: msg`Stato & download`,
      summary: msg`GET /generate/<id> per lo stato; /generate/<id>/download per il file.`,
      examples: [
        {
          lang: "bash",
          label: "cURL",
          code: 'curl -s "$ANDO_BASE_URL/generate/$GEN/download" -H "Authorization: Bearer $ANDO_API_KEY" -o out.docx',
        },
      ],
    },
  ],
};

const AREA_CAD: DocsApiArea = {
  id: "api-cad",
  title: msg`CAD (add-on manifatturiero)`,
  summary: msg`Analisi STEP/DXF, check DFM e conversione profilo→solido. Tutte asincrone → 202 con job_id e result_id.`,
  auth: msg`Runtime · Bearer <api_key> · scope cad:read / cad:write`,
  endpoints: [
    {
      id: "ep-cad-analyze",
      method: "POST",
      path: "/cad/analyze",
      scope: "cad:read",
      title: msg`Analisi STEP/DXF`,
      summary: msg`Carica un file STEP/DXF → 202. /cad/dfm-check per il DFM, /cad/convert (cad:write) per profilo DXF → solido STEP.`,
      response: {
        lang: "json",
        label: "202",
        code: '{"job_id": "…", "result_id": "…", "status": "queued"}',
      },
      examples: [
        {
          lang: "bash",
          label: "cURL",
          code: [
            'curl -s -X POST "$ANDO_BASE_URL/cad/analyze" \\',
            '  -H "Authorization: Bearer $ANDO_API_KEY" \\',
            '  -F "file=@part.step"',
          ].join("\n"),
        },
      ],
    },
    {
      id: "ep-cad-results",
      method: "GET",
      path: "/cad/results/{result_id}",
      scope: "cad:read",
      title: msg`Job & risultati`,
      summary: msg`GET /cad/jobs/<job_id> per il polling; /cad/results/<result_id> per il download.`,
      examples: [
        {
          lang: "bash",
          label: "cURL",
          code: 'curl -s "$ANDO_BASE_URL/cad/results/$RID" -H "Authorization: Bearer $ANDO_API_KEY"',
        },
      ],
    },
    {
      id: "ep-cad-search",
      method: "POST",
      path: "/cad/search",
      scope: "cad:read",
      title: msg`Ricerca capi (fashion)`,
      summary: msg`Ricerca cross-modale testo→capo (FashionSigLIP) sul corpus CAD. resource_policy.cad_visibility="tenant" allarga lo scope oltre i file della chiave.`,
      request: {
        lang: "json",
        label: "body",
        code: '{"query": "blazer lino blu", "k": 10}',
      },
      examples: [
        {
          lang: "bash",
          label: "cURL",
          code: [
            'curl -s -X POST "$ANDO_BASE_URL/cad/search" \\',
            '  -H "Authorization: Bearer $ANDO_API_KEY" -H "Content-Type: application/json" \\',
            `  -d '{"query": "blazer lino blu", "k": 10}'`,
          ].join("\n"),
        },
      ],
    },
    {
      id: "ep-cad-bom",
      method: "GET",
      path: "/cad/models/{content_item_id}/bom",
      scope: "cad:read",
      title: msg`Distinta base (BOM)`,
      summary: msg`Distinta base strutturata di un modello CAD ingerito (DXF/STEP/PDF-table federati).`,
      examples: [
        {
          lang: "bash",
          label: "cURL",
          code: 'curl -s "$ANDO_BASE_URL/cad/models/$CID/bom" -H "Authorization: Bearer $ANDO_API_KEY"',
        },
      ],
    },
  ],
};

const AREA_ACTIONS: DocsApiArea = {
  id: "api-actions",
  title: msg`Actions (system of action)`,
  summary: msg`Il write-loop: proponi un'azione su un sistema connesso, ottieni un'anteprima, poi eseguila. Parametri, permessi e rischio vengono verificati; la verifica automatica dei valori contro una fonte autorevole è ancora in preview e non va considerata una protezione attiva.`,
  auth: msg`Runtime · Bearer <api_key> · scope actions:read / actions:write`,
  endpoints: [
    {
      id: "ep-act-declare",
      method: "POST",
      path: "/connectors/{slug}/actions",
      scope: "sources:manage",
      title: msg`Dichiara un'azione`,
      summary: msg`Definisci un'azione che un connettore supporta (name, params_schema JSON, risk). GET /connectors/<slug>/actions elenca; DELETE /connectors/<slug>/actions/<name> rimuove.`,
      request: {
        lang: "json",
        label: "body",
        code: '{"name": "create_ticket", "params_schema": {"type": "object", "properties": {"title": {"type": "string"}}}, "risk": "medium"}',
      },
      examples: [
        {
          lang: "bash",
          label: "cURL",
          code: [
            'curl -s -X POST "$ANDO_BASE_URL/connectors/$SLUG/actions" \\',
            '  -H "Authorization: Bearer $ANDO_API_KEY" -H "Content-Type: application/json" \\',
            `  -d '{"name": "create_ticket", "risk": "medium"}'`,
          ].join("\n"),
        },
      ],
    },
    {
      id: "ep-act-propose",
      method: "POST",
      path: "/actions",
      scope: "actions:write",
      title: msg`Proponi un'azione`,
      summary: msg`Valida i params e crea una proposta 'proposed' con preview — nessun effetto collaterale. Idempotency-Key supportato.`,
      request: {
        lang: "json",
        label: "body",
        code: '{"connector": "helpdesk", "action": "create_ticket", "params": {"title": "Reso #123"}}',
      },
      response: {
        lang: "json",
        label: "201",
        code: '{"action_id": "act_…", "status": "proposed", "preview": {}}',
      },
      examples: [
        {
          lang: "bash",
          label: "cURL",
          code: [
            'curl -s -X POST "$ANDO_BASE_URL/actions" \\',
            '  -H "Authorization: Bearer $ANDO_API_KEY" -H "Content-Type: application/json" \\',
            `  -d '{"connector": "helpdesk", "action": "create_ticket", "params": {"title": "Reso #123"}}'`,
          ].join("\n"),
        },
      ],
    },
    {
      id: "ep-act-invoke",
      method: "POST",
      path: "/connectors/{slug}/actions/{name}/invoke",
      scope: "actions:write",
      title: msg`Invoca una capability HTTP`,
      summary: msg`Esegue subito una capability con binding HTTP. Richiede permesso sul connettore e conferma esplicita per rischio alto o critico. Per scritture live invia un Idempotency-Key stabile (massimo 255 caratteri): un esito incerto non viene ritentato automaticamente. Con una AppKey test valida i parametri senza chiamare il sistema esterno e restituisce test: true.`,
      request: {
        lang: "json",
        label: "body",
        code: '{"params":{"title":"Reso #123"},"confirm_risk":false}',
      },
      examples: [{ lang: "bash", label: "cURL", code: [
        'curl -s -X POST "$ANDO_BASE_URL/connectors/$SLUG/actions/$ACTION/invoke" \\',
        '  -H "Authorization: Bearer $ANDO_API_KEY" -H "Content-Type: application/json" \\',
        '  -H "Idempotency-Key: $INVOCATION_ID" \\',
        `  -d '{"params":{"title":"Reso #123"}}'`,
      ].join("\n") }],
    },
    {
      id: "ep-act-invocation-status",
      method: "GET",
      path: "/actions/invocations/status",
      scope: "actions:read",
      title: msg`Stato di un'invocazione diretta`,
      summary: msg`Cerca un tentativo HTTP tramite ?idempotency_key=, senza esporre parametri o risposta. Gli stati started e unknown hanno esito incerto: verifica il sistema esterno prima di creare una nuova intenzione. La lettura non ritenta né risolve il tentativo.`,
      examples: [{ lang: "bash", label: "cURL", code: 'curl -s "$ANDO_BASE_URL/actions/invocations/status?idempotency_key=$INVOCATION_ID" -H "Authorization: Bearer $ANDO_API_KEY"' }],
    },
    {
      id: "ep-act-execute",
      method: "POST",
      path: "/actions/{action_id}/execute",
      scope: "actions:write",
      title: msg`Esegui l'azione`,
      summary: msg`Dispaccia l'azione alla tua app via webhook firmato. high/critical richiedono confirm_risk=true; serve un webhook action.requested registrato. La verifica automatica contro la fonte è ancora in preview e non produce oggi un verdetto per questa API.`,
      examples: [
        {
          lang: "bash",
          label: "cURL",
          code: [
            'curl -s -X POST "$ANDO_BASE_URL/actions/$ACT/execute" \\',
            '  -H "Authorization: Bearer $ANDO_API_KEY" -H "Content-Type: application/json" \\',
            `  -d '{"confirm_risk": true}'`,
          ].join("\n"),
        },
      ],
    },
    {
      id: "ep-act-complete",
      method: "POST",
      path: "/actions/{action_id}/complete",
      scope: "actions:write",
      title: msg`Completa & poll`,
      summary: msg`La tua app riporta l'esito (completed/failed) dell'azione dispacciata. GET /actions elenca (filtro ?connector=), GET /actions/<id> per il polling.`,
      examples: [
        {
          lang: "bash",
          label: "cURL",
          code: 'curl -s "$ANDO_BASE_URL/actions/$ACT" -H "Authorization: Bearer $ANDO_API_KEY"',
        },
      ],
    },
  ],
};

const AREA_CONSOLE: DocsApiArea = {
  id: "api-console",
  title: msg`Console (management, session)`,
  summary: msg`La superficie di GOVERNO (questa console) — auth di sessione (cookie BFF), NON API-key. Overview, usage (anche per-chiave), quota, billing, team e Modelli. La API-key resta l'auth del runtime.`,
  auth: msg`Console · /platform/console/v1 · cookie di sessione (ruolo platform-dev)`,
  endpoints: [
    {
      id: "ep-con-manifest-principal-rebind",
      method: "POST",
      path: "/custom-connectors/{slug}/principal/rebind",
      scope: "session",
      title: msg`Ricollega il principal manifest`,
      summary: msg`Seleziona esplicitamente una nuova AppKey di servizio live per il sync periodico di un connettore manifest. La chiave deve appartenere al project e risolvere allo stesso utente di servizio; i contenuti già indicizzati conservano i propri ACL.`,
      request: { lang: "json", label: "body", code: '{"key_id":"<app-key-id>"}' },
      examples: [{ lang: "bash", label: "cURL", code: [
        'curl -s -X POST "$CONSOLE_BASE/custom-connectors/$SLUG/principal/rebind" \\',
        '  -b "$ANDO_SESSION_COOKIE" -H "Content-Type: application/json" \\',
        '  -d "{\\"key_id\\":\\"$KEY_ID\\"}"',
      ].join("\n") }],
    },
    {
      id: "ep-con-overview",
      method: "GET",
      path: "/overview",
      scope: "session",
      title: msg`Overview`,
      summary: msg`Contatori: documenti, chunk indicizzati, storage, connettori attivi, job recenti.`,
      examples: [
        {
          lang: "bash",
          label: "cURL",
          code: 'curl -s "$CONSOLE_BASE/overview" -b "$ANDO_SESSION_COOKIE"',
        },
      ],
    },
    {
      id: "ep-con-usage",
      method: "GET",
      path: "/usage",
      scope: "session",
      title: msg`Usage & breakdown`,
      summary: msg`Serie per giorno (7d/30d/90d): query, pagine ingerite, storage. /usage/breakdown per il consumo LLM proprio (richieste/token e %, MAI valuta). /usage/by-key ordina le chiavi per consumo; /keys/<id>/usage è il breakdown di UNA chiave (vista billing, costo incluso).`,
      examples: [
        {
          lang: "bash",
          label: "cURL",
          code: 'curl -s "$CONSOLE_BASE/usage?window=30d" -b "$ANDO_SESSION_COOKIE"',
        },
      ],
    },
    {
      id: "ep-con-quotas",
      method: "GET",
      path: "/quotas",
      scope: "session",
      title: msg`Quota (rate-limit + spend-cap)`,
      summary: msg`Sola lettura delle soglie che il runtime GIÀ enforce per-chiave: per ogni AppKey il rate-limit (limit/min, window_seconds, current_usage LIVE e remaining — null se Redis giù) e lo spend-cap mensile (monthly_limit_cents, month_to_date_cents, remaining_cents). Cifre RAW pre-markup; il markup % non è mai esposto.`,
      examples: [
        {
          lang: "bash",
          label: "cURL",
          code: 'curl -s "$CONSOLE_BASE/quotas" -b "$ANDO_SESSION_COOKIE"',
        },
      ],
    },
    {
      id: "ep-con-billing",
      method: "POST",
      path: "/billing/portal",
      scope: "session",
      title: msg`Billing (portale, fatture, crediti)`,
      summary: msg`POST /billing/portal apre il Stripe Customer Portal (metodi di pagamento, storico, PDF). GET /billing/summary è lo snapshot header (stima MTD marked-up — mai il costo raw né il markup %). GET /invoices elenca le fatture platform-usage, /invoices/<id>/pdf reindirizza al PDF Stripe. GET /credits mostra il saldo prepagato; POST /credits/checkout apre un top-up.`,
      examples: [
        {
          lang: "bash",
          label: "cURL",
          code: [
            'curl -s -X POST "$CONSOLE_BASE/billing/portal" \\',
            '  -b "$ANDO_SESSION_COOKIE" -H "Content-Type: application/json" \\',
            `  -d '{"return_url": "https://app.ando-ai.com/console/usage"}'`,
          ].join("\n"),
        },
      ],
    },
    {
      id: "ep-con-members",
      method: "GET",
      path: "/members",
      scope: "session",
      title: msg`Team (membri & inviti)`,
      summary: msg`Self-service teammate: GET /members = roster + inviti pendenti + posti (usati vs cap). /members/roles è il catalogo ruoli. POST /members/invites invita per email; .../resend re-invia, DELETE .../invites/<id> revoca. PATCH /members/<id> cambia ruolo (solo owner), DELETE /members/<id> rimuove (admin/owner). Invariante: sempre ≥ 1 owner.`,
      examples: [
        {
          lang: "bash",
          label: "cURL",
          code: [
            'curl -s -X POST "$CONSOLE_BASE/members/invites" \\',
            '  -b "$ANDO_SESSION_COOKIE" -H "Content-Type: application/json" \\',
            `  -d '{"email": "teammate@acme.com", "role": "member"}'`,
          ].join("\n"),
        },
      ],
    },
    {
      id: "ep-con-keys-lifecycle",
      method: "POST",
      path: "/keys/{key_id}/roll",
      scope: "session",
      title: msg`Rotazione & edit chiavi (session)`,
      summary: msg`Ciclo di vita chiave dalla console (create/list/revoke sono le stesse di Core, ma in sessione). POST /keys/<id>/roll conia un successore con grace window: eredita nome, scope, resource_policy e tier, e la vecchia scade a now + grace_hours (grace_hours=0 la revoca subito). PATCH /keys/<id> edita scope e/o resource_policy (spend-cap incluso) in-place, senza nuovo segreto; una chiave revocata → conflict.`,
      request: {
        lang: "json",
        label: "body",
        code: '{"grace_hours": 24}',
      },
      examples: [
        {
          lang: "bash",
          label: "cURL",
          code: [
            'curl -s -X POST "$CONSOLE_BASE/keys/$KEY/roll" \\',
            '  -b "$ANDO_SESSION_COOKIE" -H "Content-Type: application/json" \\',
            `  -d '{"grace_hours": 24}'`,
          ].join("\n"),
        },
      ],
    },
    {
      id: "ep-con-webhooks-manage",
      method: "PATCH",
      path: "/webhooks/{webhook_id}",
      scope: "session",
      title: msg`Gestione webhook (session)`,
      summary: msg`Gestione dalla console degli endpoint webhook (registrazione/test rispecchiano l'area Webhooks). PATCH /webhooks/<id> abilita/disabilita le consegne (l'unico campo mutabile), reversibile. POST /webhooks/<id>/rotate-secret conia un nuovo whsec_ mostrato UNA volta — immediate replace: il vecchio segreto smette di verificare subito (nessun overlap window), aggiorna il ricevitore prima del prossimo evento.`,
      examples: [
        {
          lang: "bash",
          label: "cURL",
          code: 'curl -s -X POST "$CONSOLE_BASE/webhooks/$WH/rotate-secret" -b "$ANDO_SESSION_COOKIE"',
        },
      ],
    },
    {
      id: "ep-con-models",
      method: "GET",
      path: "/models",
      scope: "session",
      title: msg`Modelli (per-purpose, BYOK)`,
      summary: msg`GET /models = modello corrente per purpose + catalogo. PUT /models/<purpose> sceglie; DELETE torna al default; POST /models/<purpose>/byok salva/ruota la provider key del project.`,
      examples: [
        {
          lang: "bash",
          label: "cURL",
          code: [
            'curl -s -X PUT "$CONSOLE_BASE/models/answer" \\',
            '  -b "$ANDO_SESSION_COOKIE" -H "Content-Type: application/json" \\',
            `  -d '{"provider": "vertex", "model": "gemini-3.5-flash-lite"}'`,
          ].join("\n"),
        },
      ],
    },
    {
      id: "ep-con-playground",
      method: "POST",
      path: "/query",
      scope: "session",
      title: msg`Playground`,
      summary: msg`Come il runtime ma in sessione, con as_key_id? per simulare la resource policy di una chiave specifica.`,
      examples: [
        {
          lang: "bash",
          label: "cURL",
          code: [
            'curl -s -X POST "$CONSOLE_BASE/query" \\',
            '  -b "$ANDO_SESSION_COOKIE" -H "Content-Type: application/json" \\',
            `  -d '{"query": "…", "mode": "retrieve", "k": 5, "as_key_id": "key_…"}'`,
          ].join("\n"),
        },
      ],
    },
    {
      id: "ep-con-app-users",
      method: "GET",
      path: "/embedded-connectors/apps/{app_id}/users",
      scope: "session · gestore",
      title: msg`Utenti di un'app`,
      summary: msg`Elenco paginato delle identità già attive in Ando per questa app, con stato e salute aggregata delle connessioni. Non restituisce contenuti né account provider. Gli utenti della app non sono membri del Team Console.`,
      examples: [{ lang: "bash", label: "cURL", code: 'curl -s "$CONSOLE_BASE/embedded-connectors/apps/$APP_ID/users?limit=20&offset=0" -b "$ANDO_SESSION_COOKIE"' }],
    },
    {
      id: "ep-con-app-user-revoke",
      method: "POST",
      path: "/embedded-connectors/apps/{app_id}/users/{user_id}/revoke",
      scope: "session · gestore",
      title: msg`Revoca un utente dell'app`,
      summary: msg`Revoca permanente: blocca l'identità, invalida le credenziali e rimuove i chunk personali. Il medesimo ID non viene riattivato automaticamente.`,
      examples: [{ lang: "bash", label: "cURL", code: 'curl -s -X POST "$CONSOLE_BASE/embedded-connectors/apps/$APP_ID/users/$USER_ID/revoke" -b "$ANDO_SESSION_COOKIE"' }],
    },
    {
      id: "ep-con-oidc-users",
      method: "GET",
      path: "/embedded-connectors/issuers/users",
      scope: "session · gestore",
      title: msg`Utenti OIDC verificati`,
      summary: msg`Elenco paginato delle identità per un issuer OIDC registrato, isolato dagli altri issuer. La Console offre anche la revoca per ciascun utente.`,
      examples: [{ lang: "bash", label: "cURL", code: 'curl -s -G "$CONSOLE_BASE/embedded-connectors/issuers/users" --data-urlencode "issuer=$ISSUER" --data-urlencode "limit=20" -b "$ANDO_SESSION_COOKIE"' }],
    },
  ],
};

const AREA_ENGINE_PERSONAL: DocsApiArea = {
  id: "engine-personal",
  title: msg`I miei account in Engine`,
  summary: msg`Dalla Console collega più account Gmail, Outlook, Google Drive, OneDrive, Teams o Google Chat senza Workspace. Per le chat scegli esplicitamente quali conversazioni indicizzare: senza selezione non parte alcuna sincronizzazione. Ogni account resta privato finché non concedi accesso ai contenuti indicizzati. Puoi testare l'accesso, gestire la sincronizzazione e scollegare l'account. Gli utenti della tua app usano invece un'app registrata e una AppKey server. Aruba/PEC, SharePoint, Notion e Slack non sono disponibili come connessioni private Engine.`,
  auth: msg`Console · /platform/console/v1 · cookie di sessione; nessun accesso Workspace richiesto.`,
  endpoints: [
    {
      id: "ep-engine-personal-start", method: "POST", path: "/personal-connections/sessions", scope: "session",
      title: msg`Avvia il collegamento personale`,
      summary: msg`Invia il provider e apri authorization_url. Il grant resta inattivo finché la stessa sessione Console non conferma il callback OAuth.`,
      examples: [{ lang: "bash", label: "cURL", code: [
        'curl -s -X POST "$CONSOLE_BASE/personal-connections/sessions" \\',
        '  -b "$ANDO_SESSION_COOKIE" -H "Content-Type: application/json" \\',
        `  -d '{"provider":"gmail"}'`,
      ].join("\n") }],
    },
    {
      id: "ep-engine-personal-confirm", method: "POST", path: "/personal-connections/sessions/{session_id}/confirm", scope: "session",
      title: msg`Conferma il callback OAuth`,
      summary: msg`Il browser riceve ando_session e ando_proof nell'URL di ritorno. Conferma una sola volta con la stessa sessione Console; non conservare né riutilizzare il proof.`,
      examples: [{ lang: "bash", label: "cURL", code: [
        'curl -s -X POST "$CONSOLE_BASE/personal-connections/sessions/$SESSION_ID/confirm" \\',
        '  -b "$ANDO_SESSION_COOKIE" -H "Content-Type: application/json" \\',
        `  -d '{"callback_proof":"<proof ricevuto nel callback>"}'`,
      ].join("\n") }],
    },
    {
      id: "ep-engine-personal-list", method: "GET", path: "/personal-connections", scope: "session",
      title: msg`Elenca i miei account`, summary: msg`Elenca solo gli account collegati dal membro Console autenticato.`,
      examples: [{ lang: "bash", label: "cURL", code: 'curl -s "$CONSOLE_BASE/personal-connections" -b "$ANDO_SESSION_COOKIE"' }],
    },
    {
      id: "ep-engine-personal-test", method: "POST", path: "/personal-connections/{connection_id}/test", scope: "session",
      title: msg`Testa un account`, summary: msg`Verifica che l'autorizzazione del provider sia ancora valida.`,
      examples: [{ lang: "bash", label: "cURL", code: 'curl -s -X POST "$CONSOLE_BASE/personal-connections/$CONNECTION_ID/test" -b "$ANDO_SESSION_COOKIE"' }],
    },
    {
      id: "ep-engine-personal-preview", method: "GET", path: "/personal-connections/{connection_id}/preview", scope: "session",
      title: msg`Anteprima di un account`, summary: msg`Restituisce un'anteprima limitata dell'account collegato, prima o dopo la sincronizzazione.`,
      examples: [{ lang: "bash", label: "cURL", code: 'curl -s "$CONSOLE_BASE/personal-connections/$CONNECTION_ID/preview" -b "$ANDO_SESSION_COOKIE"' }],
    },
    {
      id: "ep-engine-personal-sync-settings-get", method: "GET", path: "/personal-connections/{connection_id}/sync-settings", scope: "session",
      title: msg`Leggi il periodo email o chat`, summary: msg`Restituisce il periodo di scansione di email o chat Teams: 30 giorni per impostazione iniziale. Per Drive il periodo non si applica.`,
      examples: [{ lang: "bash", label: "cURL", code: 'curl -s "$CONSOLE_BASE/personal-connections/$CONNECTION_ID/sync-settings" -b "$ANDO_SESSION_COOKIE"' }],
    },
    {
      id: "ep-engine-personal-sync-settings-put", method: "PUT", path: "/personal-connections/{connection_id}/sync-settings", scope: "session",
      title: msg`Imposta il periodo email o chat`, summary: msg`Scegli 7, 30 o 90 giorni, oppure due date con intervallo massimo di 90 giorni. L'impostazione vale anche per le sincronizzazioni automatiche successive.`,
      examples: [{ lang: "bash", label: "cURL", code: [
        'curl -s -X PUT "$CONSOLE_BASE/personal-connections/$CONNECTION_ID/sync-settings" \\',
        '  -b "$ANDO_SESSION_COOKIE" -H "Content-Type: application/json" \\',
        `  -d '{"time_range":"30d"}'`,
      ].join("\n") }],
    },
    {
      id: "ep-engine-personal-drive-items", method: "GET", path: "/personal-connections/{connection_id}/drive-items", scope: "session",
      title: msg`Esplora Drive`, summary: msg`Elenca file e cartelle accessibili, con paginazione. Passa folder_id per entrare in una cartella.`,
      examples: [{ lang: "bash", label: "cURL", code: 'curl -s "$CONSOLE_BASE/personal-connections/$CONNECTION_ID/drive-items?page_size=100" -b "$ANDO_SESSION_COOKIE"' }],
    },
    {
      id: "ep-engine-personal-drive-selection-get", method: "GET", path: "/personal-connections/{connection_id}/drive-selection", scope: "session",
      title: msg`Leggi la selezione Drive`, summary: msg`Mostra le cartelle e i file scelti per questo account. Una nuova connessione parte senza selezione.`,
      examples: [{ lang: "bash", label: "cURL", code: 'curl -s "$CONSOLE_BASE/personal-connections/$CONNECTION_ID/drive-selection" -b "$ANDO_SESSION_COOKIE"' }],
    },
    {
      id: "ep-engine-personal-drive-selection-put", method: "PUT", path: "/personal-connections/{connection_id}/drive-selection", scope: "session",
      title: msg`Salva la selezione Drive`, summary: msg`Scegli folder_ids e file_ids. Il valore root in folder_ids autorizza la scansione dell'intero Drive accessibile. Due array vuoti disattivano la scansione.`,
      examples: [{ lang: "bash", label: "cURL", code: [
        'curl -s -X PUT "$CONSOLE_BASE/personal-connections/$CONNECTION_ID/drive-selection" \\',
        '  -b "$ANDO_SESSION_COOKIE" -H "Content-Type: application/json" \\',
        `  -d '{"folder_ids":["folder-id"],"file_ids":[]}'`,
      ].join("\n") }],
    },
    {
      id: "ep-engine-personal-chats", method: "GET", path: "/personal-connections/{connection_id}/chats", scope: "session",
      title: msg`Elenca le chat Teams`, summary: msg`Elenca chat personali e di gruppo accessibili con Chat.Read, non i canali. Richiede un account Microsoft aziendale o scolastico.`,
      examples: [{ lang: "bash", label: "cURL", code: 'curl -s "$CONSOLE_BASE/personal-connections/$CONNECTION_ID/chats?page_size=100" -b "$ANDO_SESSION_COOKIE"' }],
    },
    {
      id: "ep-engine-personal-chat-selection-get", method: "GET", path: "/personal-connections/{connection_id}/chat-selection", scope: "session",
      title: msg`Leggi la selezione chat`, summary: msg`Una nuova connessione Teams parte senza chat selezionate.`,
      examples: [{ lang: "bash", label: "cURL", code: 'curl -s "$CONSOLE_BASE/personal-connections/$CONNECTION_ID/chat-selection" -b "$ANDO_SESSION_COOKIE"' }],
    },
    {
      id: "ep-engine-personal-chat-selection-put", method: "PUT", path: "/personal-connections/{connection_id}/chat-selection", scope: "session",
      title: msg`Salva la selezione chat`, summary: msg`Scegli chat_ids; il valore all autorizza tutte le chat personali accessibili. Un array vuoto disattiva la scansione.`,
      examples: [{ lang: "bash", label: "cURL", code: [
        'curl -s -X PUT "$CONSOLE_BASE/personal-connections/$CONNECTION_ID/chat-selection" \\',
        '  -b "$ANDO_SESSION_COOKIE" -H "Content-Type: application/json" \\',
        `  -d '{"chat_ids":["chat-id"]}'`,
      ].join("\n") }],
    },
    {
      id: "ep-engine-personal-sync", method: "POST", path: "/personal-connections/{connection_id}/sync", scope: "session",
      title: msg`Avvia una pagina manualmente`, summary: msg`Opzionale: elabora una pagina nel perimetro privato del proprietario. La sincronizzazione ordinaria prosegue automaticamente; controlla sync-status. Solo se gestisci tu una scansione manuale, continua con next_page_token come page_token finché è nullo.`,
      examples: [{ lang: "bash", label: "cURL", code: [
        'curl -s -X POST "$CONSOLE_BASE/personal-connections/$CONNECTION_ID/sync" \\',
        '  -b "$ANDO_SESSION_COOKIE" -H "Content-Type: application/json" \\',
        `  -d '{"max_items":50}'`,
      ].join("\n") }],
    },
    {
      id: "ep-engine-personal-status", method: "GET", path: "/personal-connections/{connection_id}/sync-status", scope: "session",
      title: msg`Stato della sincronizzazione`, summary: msg`Mostra l'ultima riuscita, la prossima esecuzione e l'eventuale errore.`,
      examples: [{ lang: "bash", label: "cURL", code: 'curl -s "$CONSOLE_BASE/personal-connections/$CONNECTION_ID/sync-status" -b "$ANDO_SESSION_COOKIE"' }],
    },
    {
      id: "ep-engine-personal-query", method: "POST", path: "/personal-connections/query", scope: "session",
      title: msg`Cerca nei miei account`, summary: msg`Restituisce risultati indicizzati solo dagli account privati del membro Console autenticato.`,
      examples: [{ lang: "bash", label: "cURL", code: [
        'curl -s -X POST "$CONSOLE_BASE/personal-connections/query" \\',
        '  -b "$ANDO_SESSION_COOKIE" -H "Content-Type: application/json" \\',
        `  -d '{"query":"documenti del progetto","k":5}'`,
      ].join("\n") }],
    },
    {
      id: "ep-engine-personal-delete", method: "DELETE", path: "/personal-connections/{connection_id}", scope: "session",
      title: msg`Scollega un account`, summary: msg`Revoca la connessione privata e l'accesso ai suoi dati indicizzati.`,
      examples: [{ lang: "bash", label: "cURL", code: 'curl -s -X DELETE "$CONSOLE_BASE/personal-connections/$CONNECTION_ID" -b "$ANDO_SESSION_COOKIE"' }],
    },
  ],
};

const AREA_EMBEDDED_CONNECTORS: DocsApiArea = {
  id: "embedded-connectors",
  title: msg`Collega gli utenti della tua app`,
  summary: msg`Registra la tua app e l'URL di ritorno, poi crea una AppKey app_user_server con sources:manage e query:read. Dal tuo server invia l'ID stabile dell'utente autenticato: ogni persona autorizza il proprio account Gmail, Outlook, Drive, OneDrive o Teams (chat in sola lettura). La AppKey resta sul server e ogni dato in TRAMA resta isolato per app e utente. OIDC verificato è disponibile come modalità avanzata.`,
  auth: msg`Dal server: Bearer AppKey app_user_server e X-Ando-App-User-Id con ID stabile dell'utente autenticato. Non fidarti di un ID fornito dal browser senza verificarne la sessione. Per una nuova app OIDC usa una AppKey app_user_oidc vincolata all'app e X-Ando-End-User-Token; end_user_only resta per integrazioni legacy basate solo sull'issuer. Non inviare la AppKey al browser.`,
  endpoints: [
    {
      id: "ep-embedded-start",
      method: "POST",
      path: "/end-user/connection-sessions",
      scope: "sources:manage",
      title: msg`Avvia OAuth personale`,
      summary: msg`Dal server della tua app invia provider e return_url HTTPS registrato. Apri authorization_url nel browser dell'utente. Conserva l'id della sessione sul server. L'account resta inattivo fino alla conferma del callback con lo stesso ID utente autenticato.`,
      examples: [{
        lang: "bash", label: "cURL", code: [
          'curl -s -X POST "$ANDO_BASE_URL/end-user/connection-sessions" \\',
          '  -H "Authorization: Bearer $ANDO_API_KEY" \\',
          '  -H "X-Ando-App-User-Id: $APP_USER_ID" \\',
          '  -H "Content-Type: application/json" \\',
          `  -d '{"provider":"gmail","return_url":"https://app.example.com/connected"}'`,
        ].join("\n"),
      }],
    },
    {
      id: "ep-embedded-status",
      method: "GET",
      path: "/end-user/connection-sessions/{id}",
      scope: "sources:manage",
      title: msg`Controlla lo stato OAuth`,
      summary: msg`Interroga la sessione con la stessa AppKey e lo stesso ID utente. awaiting_confirmation richiede la conferma monouso del passaggio successivo; connected indica che il grant è attivo, non che i dati siano stati sincronizzati.`,
      examples: [{ lang: "bash", label: "cURL", code: 'curl -s "$ANDO_BASE_URL/end-user/connection-sessions/$SESSION_ID" -H "Authorization: Bearer $ANDO_API_KEY" -H "X-Ando-App-User-Id: $APP_USER_ID"' }],
    },
    {
      id: "ep-embedded-list",
      method: "GET",
      path: "/end-user/connections",
      scope: "sources:manage",
      title: msg`Elenca i grant personali`,
      summary: msg`Restituisce solo le connessioni dell'utente corrente della tua app.`,
      examples: [{ lang: "bash", label: "cURL", code: [
        'curl -s "$ANDO_BASE_URL/end-user/connections" \\',
        '  -H "Authorization: Bearer $ANDO_API_KEY" \\',
        '  -H "X-Ando-App-User-Id: $APP_USER_ID"',
      ].join("\n") }],
    },
    {
      id: "ep-embedded-confirm",
      method: "POST",
      path: "/end-user/connection-sessions/{id}/confirm",
      scope: "sources:manage",
      title: msg`Conferma il callback`,
      summary: msg`Il provider torna alla tua app con ando_session, ando_status=awaiting_confirmation e ando_proof. Il backend della tua app deve verificare la sessione login del browser e inviare ando_proof insieme allo stesso ID utente verificato dal tuo server. La prova dura dieci minuti e si usa una sola volta; un utente diverso non può attivare l'account. Non registrare la prova nei log.`,
      examples: [{ lang: "bash", label: "cURL", code: [
        'curl -s -X POST "$ANDO_BASE_URL/end-user/connection-sessions/$SESSION_ID/confirm" \\',
        '  -H "Authorization: Bearer $ANDO_API_KEY" \\',
        '  -H "X-Ando-App-User-Id: $APP_USER_ID" \\',
        '  -H "Content-Type: application/json" \\',
        `  -d '{"callback_proof":"'"$ANDO_PROOF"'"}'`,
      ].join("\n") }],
    },
    {
      id: "ep-embedded-revoke",
      method: "DELETE",
      path: "/end-user/connections/{connection_id}",
      scope: "sources:manage",
      title: msg`Revoca il grant della tua app`,
      summary: msg`Revoca il grant personale e cancella i token cifrati di questa connessione. Non modifica connessioni Console o Workspace.`,
      examples: [{ lang: "bash", label: "cURL", code: [
        'curl -i -X DELETE "$ANDO_BASE_URL/end-user/connections/$CONNECTION_ID" \\',
        '  -H "Authorization: Bearer $ANDO_API_KEY" \\',
        '  -H "X-Ando-App-User-Id: $APP_USER_ID"',
      ].join("\n") }],
    },
    {
      id: "ep-embedded-test",
      method: "GET",
      path: "/end-user/connections/{connection_id}/test",
      scope: "sources:manage",
      title: msg`Testa l'accesso al provider`,
      summary: msg`Verifica l'account collegato con il token dell'utente corrente; segnala quando serve una nuova autorizzazione.`,
      examples: [{ lang: "bash", label: "cURL", code: 'curl -s "$ANDO_BASE_URL/end-user/connections/$CONNECTION_ID/test" -H "Authorization: Bearer $ANDO_API_KEY" -H "X-Ando-App-User-Id: $APP_USER_ID"' }],
    },
    {
      id: "ep-embedded-preview",
      method: "GET",
      path: "/end-user/connections/{connection_id}/preview",
      scope: "sources:manage",
      title: msg`Leggi una piccola anteprima`,
      summary: msg`Restituisce al massimo cinque metadati dell'account dell'utente verificato. Non avvia la sincronizzazione né rende i contenuti cercabili.`,
      examples: [{ lang: "bash", label: "cURL", code: 'curl -s "$ANDO_BASE_URL/end-user/connections/$CONNECTION_ID/preview" -H "Authorization: Bearer $ANDO_API_KEY" -H "X-Ando-App-User-Id: $APP_USER_ID"' }],
    },
    {
      id: "ep-embedded-sync",
      method: "POST",
      path: "/end-user/connections/{connection_id}/sync",
      scope: "sources:manage",
      title: msg`Avvia una pagina manualmente`,
      summary: msg`Opzionale: elabora fino a 100 elementi con la credenziale dell'utente verificato. La scansione ordinaria è automatica: usa sync-status per seguirla. Se avvii una scansione manuale, ripeti con next_page_token fino a null; il cursore scade dopo 24 ore. I chunk restano isolati per utente e connessione, salvo accesso esplicitamente concesso al contenuto indicizzato.`,
      examples: [{ lang: "bash", label: "cURL", code: [
        'curl -s -X POST "$ANDO_BASE_URL/end-user/connections/$CONNECTION_ID/sync" \\',
        '  -H "Authorization: Bearer $ANDO_API_KEY" \\',
        '  -H "X-Ando-App-User-Id: $APP_USER_ID" \\',
        '  -H "Content-Type: application/json" \\',
        `  -d '{"max_items":50}'`,
      ].join("\n") }],
    },
    {
      id: "ep-embedded-sync-status",
      method: "GET",
      path: "/end-user/connections/{connection_id}/sync-status",
      scope: "sources:manage",
      title: msg`Stato della sincronizzazione automatica`,
      summary: msg`Mostra se l'account attende, indicizza, riconcilia, riprova dopo un errore o è aggiornato. Include l'ultimo completamento e il prossimo tentativo; non espone i cursori del provider.`,
      examples: [{ lang: "bash", label: "cURL", code: 'curl -s "$ANDO_BASE_URL/end-user/connections/$CONNECTION_ID/sync-status" -H "Authorization: Bearer $ANDO_API_KEY" -H "X-Ando-App-User-Id: $APP_USER_ID"' }],
    },
    {
      id: "ep-embedded-query",
      method: "POST",
      path: "/end-user/query",
      scope: "query:read",
      title: msg`Cerca i dati dell'utente in TRAMA`,
      summary: msg`Cerca i chunk email, Drive e chat Teams indicizzati per l'utente autenticato della tua app. Ogni risultato viene ricontrollato contro proprietario, credenziale e app attivi. Non legge le connessioni Workspace.`,
      examples: [{ lang: "bash", label: "cURL", code: [
        'curl -s -X POST "$ANDO_BASE_URL/end-user/query" \\',
        '  -H "Authorization: Bearer $ANDO_API_KEY" \\',
        '  -H "X-Ando-App-User-Id: $APP_USER_ID" \\',
        '  -H "Content-Type: application/json" \\',
        `  -d '{"query":"Quali documenti parlano del progetto?","k":10}'`,
      ].join("\n") }],
    },
    {
      id: "ep-embedded-answer",
      method: "POST",
      path: "/end-user/agentic/answer",
      scope: "query:read",
      title: msg`Rispondi con i dati autorizzati dell'utente`,
      summary: msg`Unisce contenuti indicizzati personali o condivisi e fonti strutturate con grant attivo in una risposta con citazioni. Usa la stessa identità verificata delle altre route end-user; include_rows mostra righe SQL solo al chiamante autorizzato.`,
      request: { lang: "json", label: "body", code: '{"query":"Che cosa sappiamo del progetto?","k":10,"max_steps":6}' },
      examples: [{ lang: "bash", label: "cURL", code: [
        'curl -s -X POST "$ANDO_BASE_URL/end-user/agentic/answer" \\',
        '  -H "Authorization: Bearer $ANDO_API_KEY" -H "X-Ando-App-User-Id: $APP_USER_ID" \\',
        '  -H "Content-Type: application/json" \\',
        `  -d '{"query":"Che cosa sappiamo del progetto?","k":10,"max_steps":6}'`,
      ].join("\n") }],
    },
  ],
};

export const DOCS_API_AREAS: readonly DocsApiArea[] = [
  AREA_CORE,
  AREA_QUERY,
  AREA_DOCUMENTS,
  AREA_CONVERSATIONS,
  AREA_CONNECTORS,
  AREA_ENGINE_PERSONAL,
  AREA_EMBEDDED_CONNECTORS,
  AREA_ACTIONS,
  AREA_SOURCES,
  AREA_WEBHOOKS,
  AREA_GENERATE,
  AREA_CAD,
  AREA_CONSOLE,
] as const;

// ── SDK reference ──────────────────────────────────────────────────────────────

const SDK_CLIENT: DocsSdkGroup = {
  id: "sdk-client",
  title: msg`AndoPlatform — il client`,
  summary: msg`Client sincrono, sottile su httpx (>=0.24). Context manager; ogni errore dell'envelope diventa ApiError (code, message, status, retry_after).`,
  methods: [
    {
      id: "m-init",
      signature: "AndoPlatform(api_key, base_url=DEFAULT_BASE_URL, timeout=30.0, transport=None)",
      summary: msg`Costruisce il client. api_key inviata come Authorization: Bearer.`,
      returns: "AndoPlatform (usalo come context manager).",
      example: {
        lang: "python",
        label: "Python",
        code: 'with AndoPlatform("ando_sk_live_...") as ando:\n    ...',
      },
    },
    {
      id: "m-health",
      signature: "ando.health()",
      summary: msg`Probe di liveness pubblica (nessuna auth richiesta).`,
      returns: `dict — {"status": "ok"}.`,
      example: { lang: "python", label: "Python", code: "ando.health()" },
    },
    {
      id: "m-me",
      signature: "ando.me()",
      summary: msg`Identità del project della chiave corrente.`,
      returns: `dict — {"project": {tenant_id, scopes, rate_limit_tier}}.`,
      example: { lang: "python", label: "Python", code: "ando.me()" },
    },
    {
      id: "m-upload",
      signature:
        "ando.upload_document(path_or_bytes, filename=None, content_type=None, metadata=None, partition=None)",
      summary: msg`Invia un documento per l'ingestion asincrona (scope ingest:write). Accetta un path o bytes (filename obbligatorio con i bytes).`,
      returns: `dict — {"document_id", "job_id", "status": "queued"}.`,
      example: {
        lang: "python",
        label: "Python",
        code: 'a = ando.upload_document("contratto.pdf", partition="cliente-42")',
      },
    },
    {
      id: "m-get-doc",
      signature: "ando.get_document(document_id)",
      summary: msg`Stato + metadati di un documento (scope query:read).`,
      returns: "dict — il documento.",
      example: { lang: "python", label: "Python", code: 'ando.get_document("1d4f0a9c-…")' },
    },
    {
      id: "m-del-doc",
      signature: "ando.delete_document(document_id)",
      summary: msg`Rimuove indice + storage (scope ingest:write).`,
      returns: `dict — {"deleted": true, "document_id"}.`,
      example: { lang: "python", label: "Python", code: 'ando.delete_document("1d4f0a9c-…")' },
    },
    {
      id: "m-get-job",
      signature: "ando.get_job(job_id)",
      summary: msg`Stato del job di ingestion (scope query:read).`,
      returns: `dict — {"status", "error_code", …}.`,
      example: { lang: "python", label: "Python", code: 'ando.get_job("e8a1b6d2-…")' },
    },
    {
      id: "m-wait-job",
      signature: "ando.wait_for_job(job_id, timeout=120.0, poll=2.0)",
      summary: msg`Fa il polling fino a uno stato terminale; ritenta i transienti (429/5xx/rete).`,
      returns: "dict su completed; solleva JobFailed / JobTimeout.",
      example: { lang: "python", label: "Python", code: 'ando.wait_for_job(a["job_id"])' },
    },
    {
      id: "m-query",
      signature:
        "ando.query(text, mode='retrieve', k=10, filters=None, sources=None, partition=None, include_web=False, verify=False)",
      summary: msg`Cerca nei documenti del project (scope query:read). I narrowing restringono solo; la resource policy vince.`,
      returns: `dict — {"results", "k", "count"} (+ answer/citations in mode=answer).`,
      example: {
        lang: "python",
        label: "Python",
        code: 'ando.query("condizioni di pagamento?", k=5, sources=["fs"])',
      },
    },
    {
      id: "m-answer",
      signature:
        "ando.answer(text, k=10, filters=None, sources=None, partition=None, include_web=False, verify=False)",
      summary: msg`Scorciatoia di query con mode="answer": risposta sintetizzata + citazioni.`,
      returns: `dict — {"answer", "citations", "no_results", "results"}.`,
      example: {
        lang: "python",
        label: "Python",
        code: 'r = ando.answer("durata del contratto?", k=5)\nprint(r["answer"])',
      },
    },
    {
      id: "m-agentic",
      signature:
        "ando.agentic_answer(query, k=10, sources=None, max_steps=None, with_diagnostics=False, include_rows=False, conversation_id=None, timeout=120.0)",
      summary: msg`Il loop agentico completo (PREVIEW): escalation TRAMA + text-to-SQL. Read-only. Passa conversation_id per il multi-turn.`,
      returns: `dict — {"answer", "citations", "sources_used", "steps", "diagnostics"}.`,
      example: {
        lang: "python",
        label: "Python",
        code: 'ando.agentic_answer("clienti più esposti?", sources=["structured"])',
      },
    },
    {
      id: "m-conv-create",
      signature: "ando.create_conversation(title=None)",
      summary: msg`Apre un thread di memoria per gli agentic turn (scope query:read).`,
      returns: `dict — {"conversation_id", "title", …}.`,
      example: { lang: "python", label: "Python", code: "conv = ando.create_conversation()" },
    },
    {
      id: "m-conv-list",
      signature: "ando.list_conversations(cursor=None, limit=None)",
      summary: msg`I thread della chiave, più recenti prima (cursor-paginati).`,
      returns: `dict — {"conversations", "has_more", "next_cursor"}.`,
      example: { lang: "python", label: "Python", code: "ando.list_conversations(limit=25)" },
    },
    {
      id: "m-conv-turns",
      signature: "ando.get_conversation_turns(conversation_id, cursor=None, limit=None)",
      summary: msg`Gli scambi del thread, dal più vecchio (cursor-paginati).`,
      returns: `dict — {"turns", "has_more", "next_cursor"}.`,
      example: {
        lang: "python",
        label: "Python",
        code: 'ando.get_conversation_turns("9f1c8d2e-…")',
      },
    },
    {
      id: "m-conv-delete",
      signature: "ando.delete_conversation(conversation_id)",
      summary: msg`Dimentica il thread e la sua storia (irreversibile).`,
      returns: `dict — {"deleted": true}.`,
      example: { lang: "python", label: "Python", code: 'ando.delete_conversation("9f1c8d2e-…")' },
    },
    {
      id: "m-connectors",
      signature: "ando.connectors()",
      summary: msg`Tutti i connettori del project (scope sources:manage).`,
      returns: `dict — l'elenco dei connettori.`,
      example: { lang: "python", label: "Python", code: "ando.connectors()" },
    },
    {
      id: "m-connector",
      signature: "ando.connector(slug, **options)",
      summary: msg`Un AndoConnector legato a questo slug (batching, retry, idempotency).`,
      returns: "AndoConnector.",
      example: { lang: "python", label: "Python", code: 'crm = ando.connector("acme-crm")' },
    },
    {
      id: "m-create-key",
      signature: "ando.create_key(name, scopes, live=True, expires_at=None)",
      summary: msg`Conia una nuova API key (scope keys:manage). La chiave piena è solo in questa risposta.`,
      returns: "dict — la chiave (segreto incluso una volta).",
      example: {
        lang: "python",
        label: "Python",
        code: 'ando.create_key("worker", ["query:read"], live=True)',
      },
    },
    {
      id: "m-list-keys",
      signature: "ando.list_keys()",
      summary: msg`Le chiavi del project, hash mai inclusi (scope keys:manage).`,
      returns: `dict — l'elenco delle chiavi.`,
      example: { lang: "python", label: "Python", code: "ando.list_keys()" },
    },
    {
      id: "m-revoke-key",
      signature: "ando.revoke_key(key_id)",
      summary: msg`Revoca immediata (scope keys:manage).`,
      returns: `dict — l'esito della revoca.`,
      example: { lang: "python", label: "Python", code: 'ando.revoke_key("key_…")' },
    },
  ],
};

const SDK_CONNECTOR: DocsSdkGroup = {
  id: "sdk-connector",
  title: msg`AndoConnector — Push API`,
  summary: msg`Helper legato a uno slug: batching, retry con backoff, Idempotency-Key per batch e una forma context-manager che ABORTISCE la run se il producer solleva (niente sweep parziale). La chiave deve avere sources:manage + ingest:write.`,
  methods: [
    {
      id: "m-conn-ctor",
      signature:
        "AndoConnector(client, slug, batch_size=200, max_retries=5, backoff_base=0.5, max_backoff=30.0)",
      summary: msg`Costruisce l'helper (o usa ando.connector(slug)). batch_size 1..500.`,
      returns: "AndoConnector.",
      example: {
        lang: "python",
        label: "Python",
        code: 'from ando_ai import AndoConnector\ncrm = AndoConnector(ando, "acme-crm")',
      },
    },
    {
      id: "m-conn-create",
      signature: "crm.create(name, kind='push', config=None)",
      summary: msg`Registra lo slug (POST /connectors).`,
      returns: "dict — il connettore creato.",
      example: { lang: "python", label: "Python", code: 'crm.create("Acme CRM")' },
    },
    {
      id: "m-conn-sync",
      signature: "crm.sync(records, mode='full', batch_size=None, on_rejected=None)",
      summary: msg`Esegue un'intera sync: apre, spinge tutto in batch, completa. L'iterabile è consumato pigramente (streaming di milioni di record).`,
      returns: "SyncResult — sent/accepted/unchanged/deleted/rejected/counters.",
      example: {
        lang: "python",
        label: "Python",
        code: 'res = crm.sync(fetch_deals(), mode="full")\nprint(res.accepted, res.rejected)',
      },
    },
    {
      id: "m-conn-open",
      signature: "crm.open_sync(mode='full', batch_size=None, on_rejected=None)",
      summary: msg`Apre una run e restituisce la SyncSession (context manager: complete su uscita pulita, abort su eccezione).`,
      returns: "SyncSession — push()/flush()/complete()/abort().",
      example: {
        lang: "python",
        label: "Python",
        code: 'with crm.open_sync(mode="incremental") as run:\n    run.push(records)',
      },
    },
    {
      id: "m-conn-syncs",
      signature: "crm.syncs(limit=20)",
      summary: msg`Storico delle run (GET /connectors/<slug>/syncs).`,
      returns: "dict — le run, più recenti prima.",
      example: { lang: "python", label: "Python", code: "crm.syncs(limit=20)" },
    },
    {
      id: "m-conn-disable",
      signature: "crm.disable()",
      summary: msg`Disabilita il connettore (non distruttivo).`,
      returns: `dict — l'esito.`,
      example: { lang: "python", label: "Python", code: "crm.disable()" },
    },
  ],
};

const SDK_APP_USERS: DocsSdkGroup = {
  id: "sdk-app-users",
  title: msg`Utenti della tua app — Python e TypeScript`,
  summary: msg`Usa una AppKey app_user_server solo dal backend della tua app e ricava l'ID stabile dal login verificato. Dopo OAuth scegli l'ambito da indicizzare e controlla lo stato della sync automatica; query e risposte applicano gli ACL ai contenuti propri e a quelli esplicitamente condivisi. Per una nuova app OIDC usa app_user_oidc vincolata all'app; end_user_only è legacy.`,
  methods: [
    {
      id: "m-app-user-python",
      signature: "ando.start_end_user_connection(provider, return_url, app_user_id=...)",
      summary: msg`Python: usa lo stesso app_user_id verificato in start, conferma, stato e query. Dopo OAuth la sync procede automaticamente; per Drive e Teams scegli prima cartelle, file o chat. La sync manuale a pagine resta opzionale.`,
      returns: "dict — session_id, authorization_url, expires_at.",
      example: {
        lang: "python",
        label: "Python",
        code: [
          "user_id = str(current_user.id)  # dal login verificato sul server",
          'session = ando.start_end_user_connection("gmail", RETURN_URL, app_user_id=user_id)',
          "# Apri session['authorization_url'] nel browser dell'utente.",
          "# Nel callback verifica di nuovo il login; non registrare ando_proof nei log.",
          "ando.confirm_end_user_connection(ando_session, ando_proof, app_user_id=user_id)",
          "connections = ando.list_end_user_connections(app_user_id=user_id)",
          'connection_id = connections["connections"][0]["connection_id"]',
          "status = ando.get_end_user_sync_status(connection_id, app_user_id=user_id)",
          'hits = ando.query_end_user("Email sul progetto", app_user_id=user_id)',
          'answer = ando.answer_end_user("Riassumi il progetto", app_user_id=user_id)',
        ].join("\n"),
      },
    },
    {
      id: "m-app-user-typescript",
      signature: "ando.startEndUserConnection(provider, returnUrl, { appUserId })",
      summary: msg`TypeScript: passa { appUserId } a ogni metodo; dopo OAuth controlla la sync automatica e scegli l'ambito Drive o Teams prima dell'indicizzazione. La sync manuale a pagine è opzionale. In alternativa OIDC avanzato usa { endUserToken }, mai entrambi. AppKey sempre sul server.`,
      returns: "StartedConnectionSession — session_id, authorization_url, expires_at.",
      example: {
        lang: "text",
        label: "TypeScript",
        code: [
          "const identity = { appUserId: String(currentUser.id) }; // login verificato",
          'const session = await ando.startEndUserConnection("gmail", RETURN_URL, identity);',
          "// Apri session.authorization_url nel browser dell'utente.",
          "// Nel callback verifica di nuovo il login; non registrare ando_proof.",
          "await ando.confirmEndUserConnection(andoSession, andoProof, identity);",
          "const connections = await ando.listEndUserConnections(identity);",
          "const connectionId = connections.connections[0].connection_id;",
          "const status = await ando.getEndUserSyncStatus(connectionId, identity);",
          'const hits = await ando.queryEndUser("Email sul progetto", identity);',
          'const answer = await ando.answerEndUser("Riassumi il progetto", identity);',
        ].join("\n"),
      },
    },
  ],
};

export const DOCS_SDK_GROUPS: readonly DocsSdkGroup[] = [SDK_CLIENT, SDK_CONNECTOR, SDK_APP_USERS] as const;

/** SDK exception hierarchy — surfaced as a compact reference table. */
export interface DocsSdkError {
  name: string;
  summary: MessageDescriptor;
}

export const DOCS_SDK_ERRORS: readonly DocsSdkError[] = [
  { name: "AndoPlatformError", summary: msg`Classe base di ogni errore dell'SDK.` },
  {
    name: "ApiError",
    summary: msg`Errore dell'envelope: code (stabile), message, status, retry_after (sui 429).`,
  },
  { name: "NetworkError", summary: msg`Fallimento di trasporto (DNS/connect/TLS/timeout).` },
  { name: "JobFailed", summary: msg`Un job di ingestion è finito failed (porta error_code).` },
  { name: "JobTimeout", summary: msg`wait_for_job ha superato il deadline senza stato terminale.` },
  { name: "SyncAborted", summary: msg`Push su una run già abortita/completata.` },
] as const;

/** Stable error envelope table — shared across every endpoint. */
export interface DocsErrorCode {
  http: string;
  code: string;
  when: MessageDescriptor;
}

export const DOCS_ERROR_CODES: readonly DocsErrorCode[] = [
  { http: "401", code: "invalid_api_key", when: msg`Chiave assente, malformata o sconosciuta.` },
  { http: "401", code: "expired_api_key", when: msg`expires_at superato.` },
  { http: "401", code: "revoked_api_key", when: msg`Chiave revocata.` },
  { http: "403", code: "insufficient_scope", when: msg`Scope mancante (il message elenca quali).` },
  {
    http: "429",
    code: "rate_limited",
    when: msg`Limite per-chiave superato (header Retry-After).`,
  },
  { http: "400", code: "invalid_request", when: msg`Payload non valido (dettaglio nel message).` },
  { http: "404", code: "not_found", when: msg`Risorsa inesistente nel tuo project.` },
  { http: "500", code: "internal_error", when: msg`Errore nostro; riprovabile.` },
] as const;
